<!-- ==================== VIEW MODE ==================== -->
@if (isViewMode) {
<div *ngIf="isLoading" class="loader-wrapper">
  <mat-spinner diameter="40"></mat-spinner>
</div>

<mat-card-header class="bill-header" *ngIf="!isLoading && billResult">
  <!-- Bill Number -->
  <div class="row justify-content-between m-b-12">
    <div class="col-sm-12 align-items-center">
      <h4 class="mat-subtitle-2 f-s-18 f-w-600">
        {{ 'LABEL.BILL_NO' | translate }}:
        <span class="text-primary">{{ billResult.billNo }}</span>
      </h4>
    </div>
  </div>

  <!-- Payment Summary Bar -->
  <div class="row m-b-5 align-items-center">
    <div class="col-3">
      <span class="f-w-500">{{ 'LABEL.TOTAL' | translate }}: </span>
      <span class="f-w-700">{{ billResult.amount | indianCurrency }}</span>
    </div>
    <div class="col-3">
      <span class="f-w-500 text-paid">{{ 'LABEL.PAID' | translate }}: </span>
      <span class="f-w-700 text-paid">{{ paidAmount | indianCurrency }}</span>
    </div>
    <div class="col-3">
      <span class="f-w-500 text-due">{{ 'LABEL.DUE' | translate }}: </span>
      <span class="f-w-700 text-due">{{ dueAmount | indianCurrency }}</span>
    </div>
    <div class="col-3 text-right">
      <button mat-icon-button color="primary" (click)="onPrintBill()" matTooltip="{{ 'BUTTON.BTN_PRINT' | translate }}">
        <i-tabler name="printer" class="text-primary icon-20"></i-tabler>
      </button>
    </div>
  </div>

  <!-- Patient & Doctor -->
  <div class="m-b-8">
    <span class="f-w-600 f-s-15">{{ 'LABEL.PATIENT_NAME' | translate }}:</span>
    {{ billResult.patientName }}
  </div>
  <div class="m-b-8" *ngIf="billResult.doctorName">
    <span class="f-w-600 f-s-15">{{ 'PHARMACY.PRESCRIBED_BY' | translate }}:</span>
    {{ billResult.doctorName }}
  </div>
</mat-card-header>

<mat-dialog-content *ngIf="!isLoading && billResult">
  <mat-tab-group [selectedIndex]="activeTabIndex" (selectedIndexChange)="onTabChange($event)">

    <!-- TAB 1: INVOICE DETAILS -->
    <mat-tab [label]="'TAB.BILL_DETAILS' | translate">
      <div class="tab-layout add-invoice-list">
        <div class="table-responsive">
          <table class="table table-hover b-1 rounded w-100">
            <thead>
              <tr>
                <th class="p-2 text-center" style="width: 45px;">S.No</th>
                <th class="p-2">{{ 'PHARMACY.DRUG_NAME' | translate }}</th>
                <th class="p-2">{{ 'PHARMACY.BATCH' | translate }}</th>
                <th class="p-2 text-right">{{ 'PHARMACY.QTY' | translate }}</th>
                <th class="p-2 text-right">{{ 'PHARMACY.MRP' | translate }} (&#8377;)</th>
                <th class="p-2 text-right">{{ 'PHARMACY.GST_PERCENT' | translate }}</th>
                <th class="p-2 text-right">{{ 'PHARMACY.TOTAL' | translate }} (&#8377;)</th>
              </tr>
            </thead>
            <tbody>
              @for (item of billResult.items; track item; let i = $index) {
              <tr>
                <td class="p-2 text-center">{{ i + 1 }}</td>
                <td class="p-2">{{ item.drugName }}</td>
                <td class="p-2">{{ item.batchNo }}</td>
                <td class="p-2 text-right">{{ item.quantity }}</td>
                <td class="p-2 text-right">{{ item.unitPrice | indianCurrency }}</td>
                <td class="p-2 text-right">{{ item.gstPercentage }}%</td>
                <td class="p-2 text-right f-w-600">{{ item.totalAmount | indianCurrency }}</td>
              </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </mat-tab>

    <!-- TAB 2: PAYMENTS -->
    <mat-tab>
      <ng-template mat-tab-label>
        {{ 'TAB.PAYMENTS' | translate }}
        <span *ngIf="paymentCount > 0" class="m-l-8">({{ paymentCount }})</span>
      </ng-template>

      <div class="tab-layout">
        <!-- When there are payments -->
        <ng-container *ngIf="paymentCount > 0">
          <!-- Take Payment Button (top right) -->
          <div class="row m-b-5" *ngIf="dueAmount > 0">
            <div class="col-12 text-right">
              <button mat-flat-button color="primary" (click)="takePayment()">
                <div class="d-flex align-items-center">
                  <i-tabler name="plus" class="m-r-12 icon-20"></i-tabler>
                  <span class="f-w-400 f-s-14">{{ 'BUTTON.BTN_TAKE_PAYMENT' | translate }} ({{ dueAmount |
                    indianCurrency }})</span>
                </div>
              </button>
            </div>
          </div>

          <!-- Payment History Table -->
          <div class="payment-table-scroll">
            <table mat-table [dataSource]="paymentDataSource" class="w-100 payment-table">
              <!-- Date Column -->
              <ng-container matColumnDef="paymentDate">
                <th mat-header-cell *matHeaderCellDef class="f-s-14 f-w-600">{{ 'LABEL.DATE' | translate }}</th>
                <td mat-cell *matCellDef="let element">{{ element.paymentDate | date:'dd-MMM-yyyy' }}</td>
              </ng-container>

              <!-- Method Column -->
              <ng-container matColumnDef="paymentMethod">
                <th mat-header-cell *matHeaderCellDef class="f-s-14 f-w-600">{{ 'LABEL.METHOD' | translate }}</th>
                <td mat-cell *matCellDef="let element">
                  <span class="payment-badge"
                    [ngClass]="(element.paymentTypeName === 'Cash' || !element.paymentTypeName) ? 'cash' : 'other'">
                    {{ element.paymentTypeName || 'Cash' }}
                  </span>
                </td>
              </ng-container>

              <!-- Transaction No Column -->
              <ng-container matColumnDef="transactionNo">
                <th mat-header-cell *matHeaderCellDef class="f-s-14 f-w-600">{{ 'LABEL.TRANSACTION_NO' | translate }}
                </th>
                <td mat-cell *matCellDef="let element">
                  {{ element.transactionNo | defaultValue }}
                </td>
              </ng-container>

              <!-- Description Column -->
              <ng-container matColumnDef="description">
                <th mat-header-cell *matHeaderCellDef class="f-s-14 f-w-600">{{ 'LABEL.DESCRIPTION' | translate }}</th>
                <td mat-cell *matCellDef="let element" class="ellipsis-text" [matTooltip]="element.description">
                  {{ element.description | defaultValue }}
                </td>
              </ng-container>

              <!-- Amount Column -->
              <ng-container matColumnDef="amount">
                <th mat-header-cell *matHeaderCellDef class="f-s-14 f-w-600 text-right">{{ 'LABEL.AMOUNT' | translate }}
                </th>
                <td mat-cell *matCellDef="let element" class="text-right">{{ element.amount | indianCurrency }}</td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="paymentDisplayedColumns; sticky: true"></tr>
              <tr mat-row *matRowDef="let row; columns: paymentDisplayedColumns;"></tr>
            </table>
          </div>

          <!-- Total Received -->
          <div class="row m-t-16">
            <div class="col-12 text-right">
              <span class="f-w-600">{{ 'LABEL.TOTAL_RECEIVED' | translate }}: </span>
              <span class="f-w-700 f-s-16 text-paid">{{ paidAmount | indianCurrency }}</span>
            </div>
          </div>
        </ng-container>

        <!-- Empty State - No payments yet -->
        <ng-container *ngIf="paymentCount === 0">
          <div class="text-center p-y-48 empty-state">
            <i-tabler name="receipt-off" class="icon-48 m-b-16 icon-muted"></i-tabler>
            <p class="mat-body-1 m-b-24 text-muted">{{ 'LABEL.NO_PAYMENTS_YET' | translate }}</p>
            <button mat-flat-button color="primary" (click)="takePayment()" *ngIf="dueAmount > 0">
              <div class="d-flex align-items-center">
                <i-tabler name="plus" class="icon-18 m-r-8"></i-tabler>
                {{ 'BUTTON.BTN_TAKE_PAYMENT' | translate }} ({{ dueAmount | indianCurrency }})
              </div>
            </button>
          </div>
        </ng-container>
      </div>
    </mat-tab>

  </mat-tab-group>
</mat-dialog-content>

<!-- SUMMARY SECTION (Invoice Tab Only) -->
<mat-dialog-actions *ngIf="activeTabIndex === 0 && !isLoading && billResult">
  <div class="summary-section m-r-20">
    <div class="summary-row">
      <span class="label">{{ 'PHARMACY.SUB_TOTAL' | translate }} : </span>
      <span class="value">{{ billResult.subTotal | indianCurrency }}</span>
    </div>
    <div class="summary-row">
      <span class="label">{{ 'PHARMACY.GST' | translate }} : </span>
      <span class="value">{{ billResult.totalGST | indianCurrency }}</span>
    </div>
    <div class="summary-row" *ngIf="billResult.discount">
      <span class="label">{{ 'PHARMACY.DISCOUNT' | translate }} : </span>
      <span class="value">{{ billResult.discount | indianCurrency }}</span>
    </div>
    <div class="summary-row" *ngIf="billResult.roundOff">
      <span class="label">{{ 'PHARMACY.ROUND_OFF' | translate }} : </span>
      <span class="value">{{ billResult.roundOff | number:'1.2-2' }}</span>
    </div>
    <div class="summary-row total-row">
      <span class="value total">{{ 'PHARMACY.GRAND_TOTAL' | translate }} : </span>
      <span class="value total">{{ billResult.amount | indianCurrency }}</span>
    </div>
  </div>
</mat-dialog-actions>

<!-- FOOTER ACTIONS -->
<mat-divider *ngIf="!isLoading"></mat-divider>
<mat-dialog-actions *ngIf="!isLoading">
  <div class="justify-content-between m-b-10 w-100">
    <div class="col-12 text-right">
      <a mat-stroked-button color="primary" (click)="onClose()">
        {{ 'BUTTON.BTN_CLOSE' | translate }}
      </a>
    </div>
  </div>
</mat-dialog-actions>

<!-- Hidden Receipt Component for Printing -->
<div style="display: none;">
  <app-pharmacy-receipt *ngIf="billResult" [billResult]="billResult" [hospitalInfo]="hospitalInfo"></app-pharmacy-receipt>
</div>
}

<!-- ==================== CREATE MODE ==================== -->
@if (!isViewMode) {
<h2 mat-dialog-title class="d-flex justify-content-between align-items-center">
  <span>{{ 'PHARMACY.NEW_DISPENSE' | translate }}</span>
  <button mat-icon-button (click)="onClose()">
    <i-tabler name="x" class="icon-20 d-flex"></i-tabler>
  </button>
</h2>

<!-- Patient & Doctor Selection (header, outside scrollable content) -->
<div class="dispense-header">
  <div class="row">
    <div class="col-lg-6">
      <mat-form-field appearance="outline" class="w-100">
        <mat-label>{{ 'LABEL.PATIENT_NAME' | translate }}</mat-label>
        <input type="text" matInput [formControl]="patientCtrl" [matAutocomplete]="autoPatient" />
        <mat-autocomplete #autoPatient="matAutocomplete" [displayWith]="displayPatient"
          (optionSelected)="onPatientSelected($event.option.value)">
          <mat-option *ngFor="let patient of filteredPatients$ | async" [value]="patient">
            {{ patient.name }}
          </mat-option>
        </mat-autocomplete>
        <mat-error *ngIf="patientCtrl.hasError('required')">
          {{ 'REQUIRED.SELECTED_VALUE' | translate }}
        </mat-error>
      </mat-form-field>
    </div>
    <div class="col-lg-6">
      <mat-form-field appearance="outline" class="w-100">
        <mat-label>{{ 'PHARMACY.PRESCRIBED_BY' | translate }}</mat-label>
        <input type="text" matInput [formControl]="doctorCtrl" [matAutocomplete]="autoDoctor" />
        <mat-autocomplete #autoDoctor="matAutocomplete" [displayWith]="displayDoctor"
          (optionSelected)="onDoctorSelected($event.option.value)">
          <mat-option *ngFor="let doctor of filteredDoctors$ | async" [value]="doctor">
            {{ doctor.name }}
          </mat-option>
        </mat-autocomplete>
      </mat-form-field>
    </div>
  </div>
</div>

<mat-dialog-content>
  <mat-tab-group>
    <mat-tab [label]="'PHARMACY.DISPENSE_ITEMS' | translate">
      <div [formGroup]="dispenseForm" class="tab-layout add-invoice-list">
        <div class="invoice-table-scroll">
          <div class="table-responsive">
            <table class="table table-hover b-1 rounded w-100">
              <thead>
                <tr>
                  <th class="p-1 text-center">S.No</th>
                  <th class="p-2 text-center">{{ 'PHARMACY.DRUG_NAME' | translate }}</th>
                  <th class="p-2 text-center">{{ 'PHARMACY.BATCH' | translate }}</th>
                  <th class="p-2 text-center">{{ 'PHARMACY.EXP_DATE' | translate }}</th>
                  <th class="p-2 text-center">{{ 'PHARMACY.QTY' | translate }}</th>
                  <th class="p-2 text-right">{{ 'PHARMACY.MRP' | translate }} (&#8377;)</th>
                  <th class="p-2 text-right">{{ 'PHARMACY.GST_PERCENT' | translate }}</th>
                  <th class="p-2 text-right">{{ 'PHARMACY.TOTAL' | translate }} (&#8377;)</th>
                  <th class="p-2"></th>
                </tr>
              </thead>
              <tbody formArrayName="items">
                @for (row of items.controls; track row; let i = $index; let last = $last) {
                <tr [formGroupName]="i">
                  <td class="p-1 text-center">{{ i + 1 }}</td>
                  <td class="p-2">
                    <mat-form-field appearance="outline" class="hide-hint">
                      <input type="text" matInput formControlName="drugCtrl" [matAutocomplete]="autoDrug" />
                      <mat-autocomplete #autoDrug="matAutocomplete" [displayWith]="displayDrugLookup"
                        (optionSelected)="onDrugSelected($event.option.value, items.at(i))">
                        <mat-option *ngFor="let drug of getFilteredDrugsForRow(i) | async" [value]="drug">
                          <span>{{ drug.drugName }}</span>
                          <span class="text-muted f-s-12 m-l-8">
                            MRP: {{ drug.sellingPrice | indianCurrency }} | Stock: {{ drug.totalStock }}
                          </span>
                        </mat-option>
                      </mat-autocomplete>
                    </mat-form-field>
                  </td>
                  <td class="p-2">
                    <mat-form-field appearance="outline" class="hide-hint">
                      <mat-select formControlName="selectedBatch"
                        (selectionChange)="onBatchSelected($event.value, items.at(i))">
                        <mat-option *ngFor="let batch of getAvailableBatches(row)" [value]="batch">
                          {{ batch.batchNo }} ({{ batch.quantity }})
                        </mat-option>
                      </mat-select>
                    </mat-form-field>
                  </td>
                  <td class="p-2 f-s-13 input-50">
                    <span *ngIf="row.get('expiryDate')?.value">
                      {{ row.get('expiryDate')?.value | date:'MM/yyyy' }}
                    </span>
                  </td>
                  <td class="p-2">
                    <mat-form-field appearance="outline" class="hide-hint input-50">
                      <input type="number" matInput formControlName="quantity" min="1" class="text-right"
                        (input)="onQuantityChanged(items.at(i))" />
                    </mat-form-field>
                  </td>
                  <td class="p-2 text-right f-s-14">
                    @if (row.get('drugInventoryId')?.value) {
                    {{ row.get('unitPrice')?.value | indianCurrency }}
                    } @else { — }
                  </td>
                  <td class="p-2 text-right f-s-14">
                    @if (row.get('drugInventoryId')?.value) {
                    {{ row.get('gstPercentage')?.value }}%
                    } @else { — }
                  </td>
                  <td class="p-2 text-right f-s-14 f-w-600">
                    @if (row.get('drugInventoryId')?.value) {
                    {{ row.get('totalPrice')?.value | indianCurrency }}
                    } @else { — }
                  </td>
                  <td class="p-2 whitespace-nowrap">
                    <button mat-icon-button color="primary" *ngIf="last" (click)="onAddRow()">
                      <i-tabler name="plus" class="icon-18 d-flex"></i-tabler>
                    </button>
                    <button mat-icon-button color="warn" *ngIf="i > 0" (click)="onRemoveRow(i)">
                      <i-tabler name="x" class="icon-18 d-flex"></i-tabler>
                    </button>
                  </td>
                </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </mat-tab>
  </mat-tab-group>
</mat-dialog-content>

<!-- SUMMARY SECTION (Create Mode) -->
@if (subTotal > 0) {
<mat-dialog-actions>
  <div [formGroup]="dispenseForm" class="summary-section m-r-20">
    <div class="summary-row">
      <span class="label">{{ 'PHARMACY.SUB_TOTAL' | translate }} :</span>
      <span class="value">{{ subTotal | indianCurrency }}</span>
    </div>
    <div class="summary-row">
      <span class="label">{{ 'PHARMACY.GST' | translate }} :</span>
      <span class="value">{{ totalGST | indianCurrency }}</span>
    </div>
    <div class="summary-row align-center">
      <span class="label">{{ 'PHARMACY.DISCOUNT' | translate }}</span>
      <span class="label">:</span>
      <div class="discount-box">
        <mat-form-field appearance="outline" class="input-50 hide-hint">
          <mat-select formControlName="discountType">
            <mat-option [value]="DiscountType.FixedAmount">&#8377;</mat-option>
            <mat-option [value]="DiscountType.Percentage">%</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline" class="input-100 hide-hint m-l-5">
          <input type="number" matInput formControlName="discount" class="text-right" />
        </mat-form-field>
      </div>
    </div>
    <div class="summary-row align-center">
      <span class="label">{{ 'PHARMACY.ROUND_OFF' | translate }} :</span>
      <mat-form-field appearance="outline" class="input-100 hide-hint">
        <input type="number" matInput formControlName="roundOff" class="text-right" />
      </mat-form-field>
    </div>
    <div class="summary-row total-row">
      <span class="value total">{{ 'PHARMACY.GRAND_TOTAL' | translate }} :</span>
      <span class="value total text-primary">{{ grandTotal | indianCurrency }}</span>
    </div>
  </div>
</mat-dialog-actions>
}

<mat-divider></mat-divider>
<mat-dialog-actions>
  <div class="justify-content-between m-b-10 w-100">
    <div class="col-12 text-right">
      <a mat-stroked-button color="primary" (click)="onClose()">
        {{ 'BUTTON.BTN_CLOSE' | translate }}
      </a>
      <button mat-flat-button color="primary" (click)="onSave()" [disabled]="!selectedPatientId || items.length === 0">
        {{ 'BUTTON.BTN_SAVE' | translate }}
      </button>
    </div>
  </div>
</mat-dialog-actions>

<!-- Hidden print trigger -->
<button #printBtn ngxPrint printSectionId="dispense-print-section" [styleSheetFile]="'assets/scss/print.css'"
  style="position: absolute; left: -9999px; opacity: 0; pointer-events: none;">
</button>

<!-- Print Section -->
<div style="display: none;">
  <div id="dispense-print-section" *ngIf="printMode && billResult">
    <app-pharmacy-receipt [billResult]="billResult" [hospitalInfo]="hospitalInfo"></app-pharmacy-receipt>
  </div>
</div>
}